import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Lock, CheckCircle, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setTokenError(true);
      toast.error("Invalid or missing reset token");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    // Validate password length
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setResetSuccess(true);
      toast.success("Password reset successful!");
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to reset password";
      toast.error(errorMsg);
      
      // If token expired or invalid, show error state
      if (errorMsg.includes("expired") || errorMsg.includes("Invalid")) {
        setTokenError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #e0f2fe 0%, #ffffff 50%, #cffafe 100%)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/fourpawiq-logo-gold.png" alt="FourPaw IQ" className="h-24 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>FourPaw IQ</h1>
          <p className="text-base text-gray-600">Daily operations, executive insights, and lead management — all in one platform</p>
        </div>

        <Card className="shadow-2xl border-0">
          {tokenError ? (
            <>
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Manrope' }}>
                  Invalid or Expired Link
                </CardTitle>
                <CardDescription className="text-base">
                  This password reset link is no longer valid
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center py-6 space-y-4">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="h-10 w-10 text-red-600" />
                  </div>
                  <p className="text-center text-gray-600">
                    The password reset link has expired or is invalid. Reset links are only valid for 1 hour.
                  </p>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-2">
                <Button
                  onClick={() => navigate('/forgot-password')}
                  className="w-full h-11 text-base font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                >
                  Request New Reset Link
                </Button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full text-sm font-medium text-cyan-600 hover:text-cyan-700"
                >
                  Back to Login
                </button>
              </CardFooter>
            </>
          ) : resetSuccess ? (
            <>
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Manrope' }}>
                  Password Reset Successful!
                </CardTitle>
                <CardDescription className="text-base">
                  Your password has been updated
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center py-6 space-y-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <p className="text-center text-gray-600">
                    Your password has been successfully reset. You can now login with your new password.
                  </p>
                  <p className="text-sm text-gray-500">
                    Redirecting to login page...
                  </p>
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full h-11 text-base font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                >
                  Go to Login
                </Button>
              </CardFooter>
            </>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Create New Password
                </CardTitle>
                <CardDescription className="text-base">
                  Enter your new password below
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-sm font-medium">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="newPassword"
                        type="password"
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        className="pl-10 h-11"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={6}
                        className="pl-10 h-11"
                      />
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      Password must be at least 6 characters long
                    </p>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 text-base font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                    disabled={loading}
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </Button>
                </form>
              </CardContent>
              <CardFooter className="flex justify-center">
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm font-medium text-cyan-600 hover:text-cyan-700"
                >
                  Back to Login
                </button>
              </CardFooter>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

export default ResetPassword;
